import { Box, Heading } from '@chakra-ui/layout'
import { Button, useToast } from '@chakra-ui/react'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { useGenerateFiles } from 'hooks/useGenerateFIles'
import { DocumentTypes } from 'pages/Report/types'
import { GlobalFilter } from 'pages/components/GlobalFilter'
import { useRef, useState } from 'react'
import {
    generateDashboardFinanceReportPDF,
    getFilesFinanceReports
} from 'services/report'
import { generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import ModeDashboard from './components/ModeDashboard'
import { ModeTable } from './components/ModeTable'
import { formFilter } from './form'
import { filterSchema } from './schema'
import { FinanceFilterDTO, TAnalysisViewMode, TDataViewMode } from './types'
const { getImageBase64Dashboards } = useGenerateFiles()

export const Finance = () => {
    const toast = useToast()
    const [loadingFile, setLoadingFile] = useState<boolean>(false)
    const [filter, setFilter] = useState<FinanceFilterDTO>({})
    const { isBackoffice } = useAuthContext()
    const [loading, setLoading] = useState(false)
    const { currentInstitution, currentAgent } = useInstitutionsContext()
    const [dataViewMode, setDataViewMode] = useState<TDataViewMode>('table')
    const [analysisViewMode, setAnalysisViewMode] =
        useState<TAnalysisViewMode>('synthetic')
    const refModeTable = useRef<any>(null)

    const formatFilter = (filter: FinanceFilterDTO) => {
        let formatFilter = {}
        Object.keys(filter).forEach(key => {
            formatFilter = {
                ...formatFilter,
                [key]: filter[key as keyof FinanceFilterDTO] ?? '',
                periodoInicial: filter?.periodoInicial
                    ? formatter.timeConverter(filter?.periodoInicial)
                    : '',
                periodoFinal: filter?.periodoFinal
                    ? formatter.timeConverter(filter?.periodoFinal)
                    : ''
            }
        })

        return formatFilter
    }

    const filterPayload = formatFilter(filter)

    const callAPI = (type: DocumentTypes) => {
        downloadFiles(type)
    }

    const setCurrentFilters = (value: any) => {
        setFilter(value)
        refModeTable?.current?.resetPage && refModeTable?.current?.resetPage()
    }

    const downloadFiles = async (type: DocumentTypes) => {
        try {
            setLoadingFile(true)
            if (!modeTable) {
                const imageBase64 = await getImageBase64Dashboards(
                    'finance-report'
                )

                if (imageBase64) {
                    const response = await generateDashboardFinanceReportPDF(
                        filterPayload as any,
                        imageBase64,
                        currentInstitution?.id,
                        currentAgent?.cnpj,
                        isBackoffice
                    )
                    generateFile('PDF', response)

                    toast({
                        title: 'Arquivo gerado com sucesso!',
                        description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                        position: 'bottom-left',
                        status: 'success',
                        isClosable: true
                    })

                    return
                }
                return
            }

            const response = await getFilesFinanceReports(
                {
                    ...filterPayload,
                    agrupar: analysisViewMode === 'synthetic',
                    tipo: type
                },
                currentInstitution?.id,
                currentAgent?.cnpj
            )

            generateFile(type, response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    const ANALYTICAL_VIEW_LABEL = 'Visão Analítica'
    const SYNTHETIC_VIEW_LABEL = 'Visão Sintética'

    const TABLE_VIEW_LABEL = 'Modo Tabela'
    const DASHBOARD_VIEW_LABEL = 'Modo Dashboard'

    const modeTable = dataViewMode === 'table'

    const currentAnalysisLabel =
        analysisViewMode === 'synthetic'
            ? ANALYTICAL_VIEW_LABEL
            : SYNTHETIC_VIEW_LABEL

    const currentDataViewLabel = modeTable
        ? DASHBOARD_VIEW_LABEL
        : TABLE_VIEW_LABEL

    const toggleAnalysisViewMode = () => {
        setAnalysisViewMode(prevMode =>
            prevMode === 'synthetic' ? 'analytical' : 'synthetic'
        )
    }

    const toggleDataViewMode = () => {
        setDataViewMode(prevMode =>
            prevMode === 'table' ? 'dashboard' : 'table'
        )
    }

    const customTitle = () => {
        if (!modeTable) {
            return 'Gestão Financeira'
        }

        return `Gestão Financeira (${
            analysisViewMode === 'analytical'
                ? 'Visão Analítica'
                : 'Visão Sintética'
        })`
    }

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                margin="24px 0"
                gap="8px"
            >
                <Heading size="md">{customTitle()} </Heading>
                <Box gap={'4'} flex={'row'}>
                    {modeTable && (
                        <Button
                            marginRight={'8px'}
                            colorScheme="primary"
                            type="submit"
                            onClick={toggleAnalysisViewMode}
                        >
                            {currentAnalysisLabel}
                        </Button>
                    )}
                    <Button
                        marginRight={'8px'}
                        colorScheme="primary"
                        type="submit"
                        onClick={toggleDataViewMode}
                    >
                        {currentDataViewLabel}
                    </Button>
                    <FileGenerator
                        callAPI={callAPI}
                        onlyPDF={!modeTable}
                        loading={loadingFile}
                    />
                </Box>
            </Box>

            <GlobalFilter
                schema={filterSchema}
                form={formFilter}
                actionFilter={setCurrentFilters}
                loadingFilter={loading}
                title="Filtrar Relatório(s)"
            />

            {modeTable && (
                <ModeTable
                    filter={filterPayload}
                    ref={refModeTable}
                    analysisViewMode={analysisViewMode}
                />
            )}
            {!modeTable && (
                <ModeDashboard
                    filter={filterPayload}
                    idDashboard="finance-report"
                />
            )}
        </>
    )
}
